/** Dosyasız Web Audio: hıza bağlı ray/rüzgâr uğultusu ve kısa efektler. Telefon hoparlörü için orta frekans gövdesi eklenir. */
export type StationSound = 'countdown' | 'go' | 'coin' | 'jump' | 'slide' | 'lane' | 'hit' | 'station' | 'correct' | 'wrong' | 'finish' | 'lost' | 'respawn';

const STORAGE_KEY = 'station-run-muted';
const MASTER_GAIN = 0.55;

export class StationAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private windGain: GainNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  private rumble: OscillatorNode | null = null;
  private rumbleGain: GainNode | null = null;
  private wind: AudioBufferSourceNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private muted = false;

  constructor() { try { this.muted = localStorage.getItem(STORAGE_KEY) === 'true'; } catch { /* Depolama isteğe bağlı. */ } }
  isMuted() { return this.muted; }

  unlock() {
    if (!this.context) {
      const context = new AudioContext(); this.context = context;
      const compressor = context.createDynamicsCompressor();
      compressor.threshold.value = -12; compressor.ratio.value = 10; compressor.attack.value = 0.003; compressor.release.value = 0.2;
      this.master = context.createGain(); this.master.gain.value = this.muted ? 0 : MASTER_GAIN;
      this.master.connect(compressor).connect(context.destination);
      const buffer = context.createBuffer(1, context.sampleRate * 1.5, context.sampleRate);
      const data = buffer.getChannelData(0); for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buffer;
      // Koşu rüzgârı: bant geçiren gürültü; ray uğultusu: düşük sinüs + orta gövde.
      this.windFilter = context.createBiquadFilter(); this.windFilter.type = 'bandpass'; this.windFilter.frequency.value = 500; this.windFilter.Q.value = 0.8;
      this.windGain = context.createGain(); this.windGain.gain.value = 0;
      this.wind = context.createBufferSource(); this.wind.buffer = buffer; this.wind.loop = true;
      this.wind.connect(this.windFilter).connect(this.windGain).connect(this.master); this.wind.start();
      this.rumble = context.createOscillator(); this.rumble.type = 'triangle'; this.rumble.frequency.value = 70;
      this.rumbleGain = context.createGain(); this.rumbleGain.gain.value = 0;
      this.rumble.connect(this.rumbleGain).connect(this.master); this.rumble.start();
    }
    void this.context.resume().catch(() => undefined);
  }

  toggle() {
    this.muted = !this.muted;
    try { localStorage.setItem(STORAGE_KEY, String(this.muted)); } catch { /* isteğe bağlı */ }
    if (this.master && this.context) this.master.gain.setTargetAtTime(this.muted ? 0 : MASTER_GAIN, this.context.currentTime, 0.05);
    return this.muted;
  }
  pause() { void this.context?.suspend().catch(() => undefined); }
  resume() { if (this.context) void this.context.resume().catch(() => undefined); }

  /** Her karede: koşu hızı (m/sn) rüzgâr ve uğultu seviyesini belirler. */
  run(speed: number) {
    if (!this.context || !this.windGain || !this.windFilter || !this.rumble || !this.rumbleGain) return;
    const now = this.context.currentTime, ratio = Math.min(1.3, speed / 15);
    this.windGain.gain.setTargetAtTime(0.02 + ratio * 0.09, now, 0.15);
    this.windFilter.frequency.setTargetAtTime(380 + ratio * 900, now, 0.15);
    this.rumble.frequency.setTargetAtTime(55 + ratio * 45, now, 0.2);
    this.rumbleGain.gain.setTargetAtTime(speed > 0.5 ? 0.03 + ratio * 0.04 : 0, now, 0.2);
  }

  private tone(frequency: number, at: number, duration: number, type: OscillatorType, gain: number, endFrequency?: number) {
    const context = this.context!; const oscillator = context.createOscillator(), amp = context.createGain();
    const start = context.currentTime + at;
    oscillator.type = type; oscillator.frequency.setValueAtTime(frequency, start);
    if (endFrequency) oscillator.frequency.exponentialRampToValueAtTime(endFrequency, start + duration);
    amp.gain.setValueAtTime(0, start); amp.gain.linearRampToValueAtTime(gain, start + 0.012); amp.gain.exponentialRampToValueAtTime(0.001, start + duration);
    oscillator.connect(amp).connect(this.master!); oscillator.start(start); oscillator.stop(start + duration + 0.02);
    oscillator.onended = () => { oscillator.disconnect(); amp.disconnect(); };
  }

  private noise(at: number, duration: number, type: BiquadFilterType, from: number, gain: number, to?: number) {
    if (!this.noiseBuffer) return;
    const context = this.context!; const source = context.createBufferSource(), filter = context.createBiquadFilter(), amp = context.createGain();
    const start = context.currentTime + at;
    source.buffer = this.noiseBuffer; filter.type = type; filter.frequency.setValueAtTime(from, start);
    if (to) filter.frequency.exponentialRampToValueAtTime(to, start + duration);
    amp.gain.setValueAtTime(gain, start); amp.gain.exponentialRampToValueAtTime(0.001, start + duration);
    source.connect(filter).connect(amp).connect(this.master!); source.start(start); source.stop(start + duration + 0.02);
    source.onended = () => { source.disconnect(); filter.disconnect(); amp.disconnect(); };
  }

  play(sound: StationSound) {
    if (!this.context || !this.master || this.muted) return;
    switch (sound) {
      case 'countdown': this.tone(660, 0, 0.14, 'square', 0.08); return;
      case 'go': this.tone(1320, 0, 0.35, 'square', 0.1); this.noise(0, 0.4, 'highpass', 2000, 0.06); return;
      case 'coin': this.tone(1568, 0, 0.09, 'square', 0.05); this.tone(2093, 0.05, 0.16, 'square', 0.05); return;
      case 'jump': this.noise(0, 0.22, 'bandpass', 600, 0.12, 2400); this.tone(330, 0, 0.18, 'triangle', 0.05, 660); return;
      case 'slide': this.noise(0, 0.32, 'bandpass', 2200, 0.12, 500); return;
      case 'lane': this.noise(0, 0.13, 'bandpass', 1500, 0.07); return;
      case 'hit': this.tone(120, 0, 0.4, 'triangle', 0.32, 50); this.tone(420, 0, 0.16, 'square', 0.08, 180); this.noise(0, 0.35, 'lowpass', 900, 0.4); return;
      case 'respawn': [523, 784].forEach((f, i) => this.tone(f, i * 0.08, 0.2, 'sine', 0.08)); return;
      // İstasyon anonsu: iki notalı "ding-dong".
      case 'station': this.tone(880, 0, 0.7, 'sine', 0.14); this.tone(1760, 0, 0.3, 'sine', 0.04); this.tone(698, 0.45, 0.9, 'sine', 0.14); this.tone(1396, 0.45, 0.3, 'sine', 0.04); return;
      case 'correct': [523, 659, 784, 1047].forEach((f, i) => this.tone(f, i * 0.07, 0.32, 'triangle', 0.14)); this.noise(0.05, 0.5, 'highpass', 2500, 0.07); return;
      case 'wrong': this.tone(311, 0, 0.3, 'sawtooth', 0.08, 233); this.tone(233, 0.2, 0.45, 'sawtooth', 0.08, 155); return;
      case 'lost': [392, 330, 262, 196].forEach((f, i) => this.tone(f, i * 0.18, 0.4, 'triangle', 0.12)); return;
      default: [523, 659, 784, 1047, 1319].forEach((f, i) => this.tone(f, i * 0.1, 0.5, 'triangle', 0.13));
    }
  }

  dispose() {
    try { this.wind?.stop(); this.rumble?.stop(); } catch { /* zaten durdu */ }
    void this.context?.close().catch(() => undefined);
    this.context = null; this.master = null; this.wind = null; this.rumble = null; this.noiseBuffer = null;
  }
}
